import React, { useState, useEffect } from 'react';
import {
  Globe, Send, Star, BarChart3, RefreshCw,
  CheckCircle, Sparkles, Calendar,
  MapPin, Phone, ExternalLink, Unplug
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  const token = localStorage.getItem('admin_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api${path}`, { headers: getHeaders(), ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
}

export default function AdminGBPManagement() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [connecting, setConnecting] = useState(false);
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const userRole = (adminUser.role || '').toLowerCase();

  useEffect(() => {
    fetchStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('gbp_connected') === 'true') {
      toast.success('Google Business Profile connected!');
      window.history.replaceState({}, '', window.location.pathname + '?tab=gbp-management');
    }
    if (params.get('gbp_error')) {
      toast.error('GBP connection failed: ' + params.get('gbp_error'));
      window.history.replaceState({}, '', window.location.pathname + '?tab=gbp-management');
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api('/gbp/status');
      setStatus(res);
    } catch { /* not connected */ }
    finally { setLoading(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await api('/gbp/oauth/start');
      window.location.href = res.auth_url;
    } catch (e) { toast.error(e.message || 'Failed to start connection'); setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Business Profile? You can reconnect anytime.')) return;
    try {
      await api('/gbp/disconnect', { method: 'POST' });
      toast.success('GBP disconnected');
      setStatus(null);
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="gbp-mgmt-loading">
      <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
    </div>
  );

  if (!status?.connected) return (
    <div className="max-w-lg mx-auto text-center py-16 space-y-6" data-testid="gbp-connect-page">
      <Globe className="w-16 h-16 text-blue-600 mx-auto" />
      <h1 className="text-2xl font-bold text-slate-800">Google Business Profile</h1>
      <p className="text-slate-500">Connect your Google account to manage GBP listings for all your clients — create posts, respond to reviews, and track performance from one dashboard.</p>
      <button
        onClick={connect}
        disabled={connecting}
        className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        data-testid="gbp-connect-btn"
      >
        {connecting ? 'Connecting...' : 'Connect Google Business Profile'}
      </button>
      <p className="text-xs text-slate-400">Sign in with the Google account that has Manager access to your clients' business listings (e.g. larryscala57@gmail.com).</p>
    </div>
  );

  const loc = status?.selected_location || status?.locations?.[0];
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'posts', label: 'Posts', icon: Send },
    { id: 'reviews', label: 'Reviews', icon: Star },
  ];

  return (
    <div className="space-y-6" data-testid="gbp-management-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Google Business Profile</h1>
          {loc && <p className="text-sm text-slate-500 mt-1">{loc.title} — {loc.storefrontAddress?.addressLines?.[0]}, {loc.storefrontAddress?.locality}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Connected</span>
          <a href={loc?.websiteUri || 'https://business.google.com'} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> View on Google
          </a>
          {userRole === 'superadmin' && (
            <button onClick={disconnect} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-testid="gbp-disconnect-btn">
              <Unplug className="w-3 h-3" /> Disconnect
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`gbp-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Icon className="w-4 h-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Location Selector — only when multiple locations and no selection */}
      {!status?.selected_location && status?.locations?.length > 1 && (
        <LocationSelector locations={status.locations} selected={status.selected_location} onSelect={async (locName) => {
          try {
            await api('/gbp/select-location', { method: 'POST', body: JSON.stringify({ location_name: locName }) });
            toast.success('Location selected!');
            fetchStatus();
          } catch (e) { toast.error(e.message); }
        }} />
      )}

      {activeTab === 'overview' && <OverviewTab status={status} onRefresh={fetchStatus} />}
      {activeTab === 'posts' && <PostsTab businessName={loc?.title || ''} />}
      {activeTab === 'reviews' && <ReviewsTab businessName={loc?.title || ''} />}
    </div>
  );
}

function LocationSelector({ locations, selected, onSelect }) {
  return (
    <div className="bg-white p-5 rounded-lg border border-slate-200" data-testid="location-selector">
      <h3 className="font-semibold text-slate-800 mb-3">Select Business Location</h3>
      <p className="text-sm text-slate-500 mb-4">Your account has access to {locations.length} locations. Select the one you want to manage:</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {locations.map((loc, i) => {
          const isSelected = selected?.name === loc.name;
          const addr = loc.storefrontAddress;
          return (
            <button
              key={i}
              onClick={() => onSelect(loc.name)}
              className={`p-4 border-2 rounded-lg text-left transition-all ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
              data-testid={`loc-select-${i}`}
            >
              <p className="font-medium text-slate-800">{loc.title}</p>
              {addr && <p className="text-xs text-slate-500 mt-1">{addr.addressLines?.[0]}, {addr.locality}, {addr.administrativeArea}</p>}
              {isSelected && <span className="inline-block mt-2 text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-medium">Active</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OverviewTab({ status, onRefresh }) {
  const loc = status?.selected_location || status?.locations?.[0];

  return (
    <div className="space-y-6">
      {loc && (
        <div className="bg-white p-6 rounded-lg border border-slate-200">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-slate-800">{loc.title}</h2>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                {loc.storefrontAddress && (
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {loc.storefrontAddress.addressLines?.[0]}, {loc.storefrontAddress.locality}, {loc.storefrontAddress.administrativeArea} {loc.storefrontAddress.postalCode}</span>
                )}
              </div>
              {loc.phoneNumbers?.primaryPhone && (
                <span className="flex items-center gap-1 text-sm text-slate-500"><Phone className="w-4 h-4" /> {loc.phoneNumbers.primaryPhone}</span>
              )}
              {loc.websiteUri && (
                <a href={loc.websiteUri} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  <Globe className="w-4 h-4" /> {loc.websiteUri}
                </a>
              )}
            </div>
            <button onClick={onRefresh} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="gbp-refresh-btn">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickPostCard businessName={loc?.title || ''} />
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-3">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Star className="w-5 h-5 text-yellow-500" /> Reviews</h3>
          <p className="text-sm text-slate-500">Monitor and respond to customer reviews to build trust and improve local SEO.</p>
          <p className="text-xs text-slate-400">Tip: Responding to reviews within 24 hours boosts your GBP ranking.</p>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-3">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-500" /> Performance</h3>
          <p className="text-sm text-slate-500">Your GBP listing helps customers find you on Google Search and Maps.</p>
          <p className="text-xs text-slate-400">Keep your profile active with regular posts and review responses.</p>
        </div>
      </div>

      <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 text-xs text-slate-500">
        <p>Connected since: {status?.connected_at ? new Date(status.connected_at).toLocaleDateString() : 'N/A'}</p>
        <p>Locations: {status?.locations?.length || 0} | Accounts: {status?.accounts?.length || 0}</p>
      </div>
    </div>
  );
}

function QuickPostCard({ businessName }) {
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api('/gbp/posts', { method: 'POST', body: JSON.stringify({ summary: text }) });
      toast.success('Published to Google Business Profile!');
      setText('');
    } catch (e) { toast.error(e.message); }
    finally { setPosting(false); }
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-3">
      <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" /> Quick Post</h3>
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Share an update..." rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-blue-500 outline-none" />
      <button onClick={post} disabled={posting || !text.trim()} className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50" data-testid="gbp-quick-post-btn">
        {posting ? 'Publishing...' : 'Publish'}
      </button>
    </div>
  );
}

function PostsTab({ businessName }) {
  const [posts, setPosts] = useState([]);
  const [postText, setPostText] = useState('');
  const [posting, setPosting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [topicType, setTopicType] = useState('STANDARD');
  const [ctaUrl, setCtaUrl] = useState('');

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await api('/gbp/posts');
      setPosts(res.posts || []);
    } catch { /* empty */ }
  };

  const createPost = async () => {
    if (!postText.trim()) return;
    setPosting(true);
    try {
      const body = { summary: postText, topic_type: topicType };
      if (ctaUrl.trim()) {
        body.call_to_action = { actionType: 'LEARN_MORE', url: ctaUrl };
      }
      await api('/gbp/posts', { method: 'POST', body: JSON.stringify(body) });
      toast.success('Post published!');
      setPostText(''); setCtaUrl('');
      fetchPosts();
    } catch (e) { toast.error(e.message); }
    finally { setPosting(false); }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const res = await api('/gbp/ai-generate-post', {
        method: 'POST',
        body: JSON.stringify({ business_name: businessName }),
      });
      setPostText(res.text || '');
      toast.success('AI draft generated! Edit and publish.');
    } catch (e) { toast.error(e.message); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 text-lg">Create GBP Post</h3>
          <button onClick={generateWithAI} disabled={generating} className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-md text-xs font-medium hover:bg-purple-100 disabled:opacity-50" data-testid="gbp-ai-generate-btn">
            <Sparkles className="w-3.5 h-3.5" /> {generating ? 'Generating...' : 'AI Generate'}
          </button>
        </div>

        <div className="flex gap-2 mb-2">
          {[
            { id: 'STANDARD', label: 'Update' },
            { id: 'OFFER', label: 'Offer' },
            { id: 'EVENT', label: 'Event' },
          ].map(t => (
            <button key={t.id} onClick={() => setTopicType(t.id)} className={`px-3 py-1 rounded-md text-xs font-medium ${topicType === t.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <textarea value={postText} onChange={e => setPostText(e.target.value)} placeholder="Write your Google Business Profile post... (promotions, events, updates)" rows={4} className="w-full px-4 py-3 border border-slate-200 rounded-md text-sm focus:border-blue-500 outline-none" maxLength={1500} data-testid="gbp-post-textarea" />
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{postText.length}/1500</span>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Call-to-Action URL (optional)</label>
          <input value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="https://yourbusiness.com/contact" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-blue-500 outline-none" />
        </div>

        <button onClick={createPost} disabled={posting || !postText.trim()} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-md disabled:opacity-50" data-testid="gbp-publish-btn">
          {posting ? 'Publishing...' : 'Publish to Google'}
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Post History</h3>
          <button onClick={fetchPosts} className="text-xs text-blue-600 hover:underline"><RefreshCw className="w-3 h-3 inline mr-1" />Refresh</button>
        </div>
        {posts.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No posts yet. Create your first GBP post above!</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {posts.map((p, i) => (
              <div key={i} className="p-4 hover:bg-slate-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{p.summary}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.created_at).toLocaleDateString()}</span>
                      <span>{p.created_by}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${p.topic_type === 'OFFER' ? 'bg-green-100 text-green-700' : p.topic_type === 'EVENT' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{p.topic_type || 'UPDATE'}</span>
                    </div>
                  </div>
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewsTab({ businessName }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avgRating, setAvgRating] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [replyText, setReplyText] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(null);

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await api('/gbp/reviews');
      setReviews(res.reviews || []);
      setAvgRating(res.averageRating || 0);
      setTotalCount(res.totalReviewCount || 0);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const replyToReview = async (reviewName) => {
    const reviewId = reviewName.split('/').pop();
    const comment = replyText[reviewId];
    if (!comment?.trim()) return;
    setReplyingTo(reviewId);
    try {
      await api(`/gbp/reviews/${reviewId}/reply`, { method: 'POST', body: JSON.stringify({ comment }) });
      toast.success('Reply posted!');
      setReplyText(prev => ({ ...prev, [reviewId]: '' }));
      fetchReviews();
    } catch (e) { toast.error(e.message); }
    finally { setReplyingTo(null); }
  };

  const generateAIReply = async (review) => {
    const reviewId = review.name.split('/').pop();
    setAiGenerating(reviewId);
    try {
      const res = await api('/gbp/ai-generate-reply', {
        method: 'POST',
        body: JSON.stringify({
          reviewer_name: review.reviewer?.displayName || 'Customer',
          star_rating: review.starRating,
          comment: review.comment || '',
          business_name: businessName,
        }),
      });
      setReplyText(prev => ({ ...prev, [reviewId]: res.text || '' }));
      toast.success('AI reply drafted! Review and post.');
    } catch { toast.error('AI generation failed'); }
    finally { setAiGenerating(null); }
  };

  const starToNum = (s) => s === 'FIVE' ? 5 : s === 'FOUR' ? 4 : s === 'THREE' ? 3 : s === 'TWO' ? 2 : 1;

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500 mb-1">Average Rating</p>
          <div className="flex items-center gap-2">
            <p className="text-3xl font-bold text-slate-800">{avgRating.toFixed(1)}</p>
            <span className="text-yellow-500 text-lg">{'*'.repeat(Math.round(avgRating))}</span>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500 mb-1">Total Reviews</p>
          <p className="text-3xl font-bold text-slate-800">{totalCount}</p>
        </div>
        <div className="bg-white p-5 rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500 mb-1">Needs Reply</p>
          <p className="text-3xl font-bold text-slate-800">{reviews.filter(r => !r.reviewReply).length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Google Reviews</h3>
          <button onClick={fetchReviews} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {reviews.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No reviews found. They'll appear here once the GBP listing has reviews.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {reviews.map((r, i) => {
              const reviewId = r.name?.split('/').pop() || String(i);
              const stars = starToNum(r.starRating);
              return (
                <div key={i} className="p-5" data-testid={`review-${reviewId}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white font-semibold text-sm">
                        {(r.reviewer?.displayName || 'A').charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{r.reviewer?.displayName || 'Anonymous'}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-yellow-500 text-sm">{'*'.repeat(stars)}{'*'.repeat(5 - stars)}</span>
                          <span className="text-xs text-slate-400">{r.createTime ? new Date(r.createTime).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                    </div>
                    {!r.reviewReply && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Needs Reply</span>
                    )}
                  </div>

                  {r.comment && <p className="text-sm text-slate-600 mb-3 leading-relaxed">{r.comment}</p>}

                  {r.reviewReply && (
                    <div className="ml-6 p-3 bg-blue-50 rounded-lg border border-blue-100 mb-3">
                      <p className="text-xs font-medium text-blue-700 mb-1">Your Reply:</p>
                      <p className="text-sm text-blue-900">{r.reviewReply.comment}</p>
                      <p className="text-xs text-blue-400 mt-1">{r.reviewReply.updateTime ? new Date(r.reviewReply.updateTime).toLocaleDateString() : ''}</p>
                    </div>
                  )}

                  {!r.reviewReply && (
                    <div className="ml-6 space-y-2">
                      <div className="flex gap-2">
                        <input
                          value={replyText[reviewId] || ''}
                          onChange={e => setReplyText(prev => ({ ...prev, [reviewId]: e.target.value }))}
                          placeholder="Write a professional reply..."
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-blue-500 outline-none"
                          data-testid={`reply-input-${reviewId}`}
                        />
                        <button
                          onClick={() => generateAIReply(r)}
                          disabled={aiGenerating === reviewId}
                          className="flex items-center gap-1 px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-md text-xs font-medium hover:bg-purple-100 disabled:opacity-50"
                          data-testid={`ai-reply-${reviewId}`}
                        >
                          <Sparkles className="w-3.5 h-3.5" /> {aiGenerating === reviewId ? '...' : 'AI Draft'}
                        </button>
                        <button
                          onClick={() => replyToReview(r.name)}
                          disabled={replyingTo === reviewId || !replyText[reviewId]?.trim()}
                          className="px-4 py-2 bg-slate-800 text-white text-sm rounded-md disabled:opacity-50"
                          data-testid={`post-reply-${reviewId}`}
                        >
                          {replyingTo === reviewId ? '...' : 'Reply'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
